import React, {useState, useCallback, useEffect} from "react";
import {useDispatch} from "react-redux";
import PropTypes from "prop-types";
import ArgoColorInputHook from "./ArgoColorInputHook";
import ArgoSelectPortalTextDisplayTypeHook from "./ArgoSelectPortalTextDisplayTypeHook";
import ArgoTextDisplayCustomHook from "./ArgoTextDisplayCustomHook";
import ArgoCheckboxHook from "./ArgoCheckboxHook";
import ArgoTextFieldHook from "./ArgoTextFieldHook";
import ArgoTextFieldSelectHook from "./ArgoTextFieldSelectHook";
import ArgoLikertAnswerGeneratorHook from "./ArgoLikertAnswerGeneratorHook";
import * as TEMPLATE from "../../constants/templateConstants";
import * as portalShared from "../../portal/shared/metadataConstantsAndUtilities";
import * as portalTypes from "../../portal/constants/actionConstants";

// Color title/prop arrays (unchanged from class component for parity)
const customColorRadiosAndCheckboxesTitles = [
    "Control (Unselected)",
    "Control (Selected)",
    "Control (Hover)",
    "Control (Hover Selected)",
    "Text (Unselected)",
    "Text (Selected)",
    "Text (Hover)",
    "Text (Hover Selected)"
];

const customColorRadiosAndCheckboxesProps = [
    "unselectedControl",
    "selectedControl",
    "hoverControl",
    "hoverSelectedControl",
    "unselectedText",
    "selectedText",
    "hoverText",
    "hoverSelectedText"
];

const customColorTitlesOutlined = [
    "Control (Unselected)",
    "Control (Selected)",
    "Control (Hover)",
    "Control (Hover Selected)",
    "Border (Unselected)",
    "Border (Selected)",
    "Border (Hover)",
    "Border (Hover Selected)",
    "Background (Unselected)",
    "Background (Selected)",
    "Background (Hover)",
    "Background (Hover Selected)",
    "Text (Unselected)",
    "Text (Selected)",
    "Text (Hover)",
    "Text (Hover Selected)"
];

const customColorPropsOutlined = [
    "unselectedControl",
    "selectedControl",
    "hoverControl",
    "hoverSelectedControl",
    "unselectedBorder",
    "selectedBorder",
    "hoverBorder",
    "hoverSelectedBorder",
    "unselectedBackground",
    "selectedBackground",
    "hoverBackground",
    "hoverSelectedBackground",
    "unselectedText",
    "selectedText",
    "hoverText",
    "hoverSelectedText"
];

const customColorTitlesNoControl = [
    "Border (Unselected)",
    "Border (Selected)",
    "Border (Hover)",
    "Border (Hover Selected)",
    "Background (Unselected)",
    "Background (Selected)",
    "Background (Hover)",
    "Background (Hover Selected)",
    "Text (Unselected)",
    "Text (Selected)",
    "Text (Hover)",
    "Text (Hover Selected)"
];

const customColorPropsNoControl = [
    "unselectedBorder",
    "selectedBorder",
    "hoverBorder",
    "hoverSelectedBorder",
    "unselectedBackground",
    "selectedBackground",
    "hoverBackground",
    "hoverSelectedBackground",
    "unselectedText",
    "selectedText",
    "hoverText",
    "hoverSelectedText"
];

const customColorTitlesDDLB = [
    "Floating Label (Unselected)",
    "Floating Label (Hover)",
    "Floating Label (Selected)",
    "Selected Text",
    "Border (Unselected)",
    "Border (Hover)",
    "Border (Selected)",
    "After Selection Border",
    "Background (Unselected)",
    "Background (Selected)",
    "Background (Hover)"
];

const customColorPropsDDLB = [
    "unselectedFloatingLabel",
    "hoverFloatingLabel",
    "selectedFloatingLabel",
    "selectedText",
    "unselectedBorder",
    "hoverBorder",
    "selectedBorder",
    "ddlbBorderColorAfterSelection",
    "unselectedBackground",
    "selectedBackground",
    "hoverBackground"
];

const customColorTitlesFreeText = [
    "Place Holder",
    "Text (Unselected)",
    "Text (Hover)",
    "Text (Selected)",
    "Floating Label (Unselected)",
    "Floating Label (Hover)",
    "Floating Label (Selected)",
    "Border (Unselected)",
    "Border (Hover)",
    "Border (Selected)",
    "Background (Unselected)",
    "Background (Selected)",
    "Background (Hover)"
];

const customColorPropsFreeText = [
    "placeHolder",
    "unselectedText",
    "selectedText",
    "hoverText",
    "unselectedFloatingLabel",
    "hoverFloatingLabel",
    "selectedFloatingLabel",
    "unselectedBorder",
    "hoverBorder",
    "selectedBorder",
    "unselectedBackground",
    "selectedBackground",
    "hoverBackground"
];

const customColorTitlesRanking = [
    "Text",
    "Text (Hover)",
    "Numbers",
    "Numbers (Hover)",
    "Border",
    "Border (Hover)",
    "Background",
    "Background (Hover)",
    "Paper Background",
    "Drag Icon",
    "Drag Icon (Hover)",
    "Button",
    "Button (Hover)",
    "Button Text",
    "Button Text (Hover)"
]

const customColorPropsRanking = [
    "text",
    "hoverText",
    "numbers",
    "hoverNumbers",
    "borderColor",
    "hoverBorder",
    "backgroundColor",
    "hoverBackground",
    "paperBackgroundColor",
    "dragIconColor",
    "hoverDragIconColor",
    "buttonObj.buttonColor",
    "buttonObj.hoverButtonColor",
    "buttonObj.buttonTextColor",
    "buttonObj.hoverButtonTextColor"
]

// Select lists (unchanged)
const styleList = [
    { value: "outlined", text: "Outlined" },
    { value: "standard", text: "Standard" },
    { value: "filled", text: "Filled" }
];

const sizeList = [
    { value: "small", text: "Small" },
    { value: "medium", text: "Medium" }
];

const widthFormatList = [
    { value: "percentage", text: "Percentage" },
    { value: "pixels", text: "Pixels" }
];

const labelPositionList = [
    { value: "none", text: "None" },
    { value: "above", text: "Above" },
    { value: "below", text: "Below" }
];

const arrangeList = [
    { value: "row", text: "One per Row" },
    { value: "side", text: "Side by Side" }
];

const defaultOrCustomList = [
    { value: "default", text: "Default" },
    { value: "custom", text: "Custom" }
];

const zipFormatList = [
    { value: "#####", text: "Standard (5 Digit)" },
    { value: "#####-####", text: "Extended (9 Digit)" }
];

const decimalPlacesList = [
    { value: 1, text: "1" },
    { value: 2, text: "2" },
    { value: 3, text: "3" },
    { value: 4, text: "4" },
    { value: 5, text: "5" },
    { value: 6, text: "6" }
];

function ArgoQuestionFormatCustom(props) {
    const [formatObj, setFormatObj] = useState(props.initToObj);

    const dispatch = useDispatch()
    // --- Metadata propagation wrapper (parity with class setState callback logic) ---
    const setMetadataValue = useCallback(
        (nextFormat) => {
            let mdTempObj = null;
            const qType = props.questionType;
            switch (qType) {
                case portalShared.FREE_TEXT:
                case portalShared.EMAIL:
                case portalShared.PHONE:
                case portalShared.NUMBER:
                case portalShared.CURRENCY:
                case portalShared.DATE:
                case portalShared.DATE_RANGE:
                case portalShared.ZIPPOSTAL_ONLY:
                    mdTempObj = portalShared.setCustomQuestionTextObj(nextFormat);
                    break;
                case portalShared.RADIO:
                case portalShared.YES_NO:
                case portalShared.STATE_ONLY:
                    mdTempObj = portalShared.setCustomQuestionSingleSelectObj(nextFormat);
                    break;
                case portalShared.CHECKBOX:
                    mdTempObj = portalShared.setCustomQuestionCheckboxObj(nextFormat);
                    break;
                case portalShared.ADDRESS_FULL:
                    mdTempObj = portalShared.setCustomQuestionAddressObj(nextFormat);
                    break;
                case portalShared.RANKING:
                    mdTempObj = portalShared.setCustomQuestionRankingObj(nextFormat);
                    break;
                default:
                    mdTempObj = nextFormat;
            }
            const mpo = props.metadataPropsObj;
            setFormatObj(mdTempObj);
            if (mpo && mpo.setCustomMetadataFunction) {
                mpo.setCustomMetadataFunction(
                    null,
                    mpo.template,
                    mpo.templateUpdateFunction,
                    mpo.setStateCallbackFunction,
                    props.fieldName,
                    "custom",
                    mpo.metadataTag,
                    mdTempObj,
                    mpo.depth
                );
            }
        },
        [props.metadataPropsObj, props.fieldName, props.questionType]
    );

    const applyFormatObj = useCallback(
        (nextObj) => {
            // First update UI state locally, then propagate metadata
            setFormatObj(nextObj);
            setMetadataValue(nextObj);
        },
        [setMetadataValue]
    );

    useEffect(() => {

        if (props.metadataPropsObj.template.metadata.hasOwnProperty(portalShared.CUSTOM_DDO_QUE_FORMAT)) {
            let stringObj = props.metadataPropsObj.template.metadata[portalShared.CUSTOM_DDO_QUE_FORMAT];
            if (JSON.stringify(formatObj) !== stringObj) {
                setFormatObj(JSON.parse(stringObj));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[props.metadataPropsObj.template.metadata]);

    // Text change (includes nested address object dot notation logic)
    const onTextChange = (event, fieldName) => {
        const value = event.target.value;
        const tempObj = { ...formatObj };

        if (fieldName.includes(".")) {
            const [parent, child] = fieldName.split(".");
            if (child === "floatingLabel") {
                tempObj[parent].floatingLabel.text = value;
            } else {
                tempObj[parent][child] = value;
            }

            if (child === "widthEntry") {
                tempObj[parent].width =
                    value + (tempObj[parent].widthType === "pixels" ? "px" : "%");
            }
        } else {
            tempObj[fieldName] = value;
            if (fieldName === "widthEntry") {
                tempObj.width = value + (tempObj.widthType === "pixels" ? "px" : "%");
            }
        }
        if (fieldName === "indent") {
            tempObj.indent = value + "px";
        }
        applyFormatObj(tempObj);
    };

    // Select change logic (parity with class component)
    const onSelectChange = (event, fieldName) => {
        const selected = ["alignAnswersRight", "alignQuestionsAndAnswers", "borderEnabled", "numbersEnabled", "showDragIcon"].includes(fieldName) ? event.target.checked : event.target.value;
        const tempObj = portalShared.cloneObj(formatObj); // formatObj is the custom question metadata for the question type
        if (fieldName.includes(".")) {
            const [parent, child] = fieldName.split(".");
            tempObj[parent][child] = selected;
            if (child === "widthType") {
                const entry = tempObj[parent].widthEntry;
                tempObj[parent].width = entry + (selected === "pixels" ? "px" : "%");
            }
        } else {
            tempObj[fieldName] = selected;
        }

        setTimeout(() => {
            dispatch({type: portalTypes.INCREMENT_PORTAL_COUNT});
        }, 500);

        switch (props.questionType) {
            case portalShared.FREE_TEXT:
            case portalShared.EMAIL:
            case portalShared.PHONE:
            case portalShared.NUMBER:
            case portalShared.CURRENCY:
            case portalShared.DATE:
            case portalShared.DATE_RANGE:
            case portalShared.ZIPPOSTAL_ONLY:
                switch (fieldName) {
                    case "labelPosition":
                        if (selected === "none") {
                            tempObj.label = "";
                            tempObj.label2 = ""; // date-range second label
                        }
                        break;
                    case "color":
                        if (selected === "default") {
                            portalShared.setQuestionTextDefaultColors(tempObj); // Must set colors back to their default values
                        }
                        break;
                    default:
                        break;
                }
                break;
            case portalShared.ADDRESS_FULL:
                switch (fieldName) {
                    case "labelPosition":
                        if (selected === "none") {
                            tempObj.label = "";
                            tempObj.label2 = ""; // date-range second label
                        }
                        break;
                    case "color":
                        if (selected === "default") {
                            portalShared.setAddressDefaultColors(tempObj); // Must set colors back to their default values
                        }
                        break;
                    default:
                        break;
                }
                break;
            case portalShared.STATE_ONLY:
            case portalShared.RADIO:
            case portalShared.YES_NO:
                switch (fieldName) {
                    case "displayAs":
                        if (selected === "single") {
                            tempObj.style = tempObj.style === "" ? "radios" : tempObj.style;
                        } else {
                            tempObj.style = ""; // likert clears style
                        }
                        break;
                    case "style":
                        tempObj.color = "default"; // reset color
                        portalShared.setSingleSelectDefaultColors(tempObj, selected);
                        tempObj.arrange = "row"
                        tempObj.width = "100%"
                        tempObj.widthEntry = "100"
                        tempObj.widthType = "percentage"
                        tempObj.alignAnswersRight = false
                        tempObj.alignQuestionsAndAnswers = false
                        tempObj.indent = "0px"
                        break;
                    case "titleType":
                        if (selected === "none") {
                            tempObj.titleLeft = "";
                            tempObj.titleCenter = "";
                            tempObj.titleRight = "";
                        }
                        break;
                    case "color":
                        if (selected === "default") {
                            portalShared.setSingleSelectDefaultColors(tempObj, tempObj.style); // Must set colors back to their default values
                        }
                        break;
                    case "arrange":
                        if (selected === "row") {
                            tempObj.alignAnswersRight = false
                            tempObj.alignQuestionsAndAnswers = false
                            tempObj.indent = "0px"
                        }
                        break;
                    case "alignAnswersRight":
                        if (selected === false) {
                            tempObj.alignQuestionsAndAnswers = false
                        }
                        break;
                    default:
                        break;
                }
                break;
            case portalShared.CHECKBOX:
                switch (fieldName) {
                    case "color":
                            if (selected === "default") {
                                portalShared.setCheckboxDefaultColors(tempObj); // Must set colors back to their default values
                            }
                            break;
                    default:
                        break;
                }
                break;
            case portalShared.RANKING:
                if (!["borderEnabled", "numbersEnabled"].includes(fieldName)) portalShared.setRankingDefaultColors(tempObj)
                break;
            case portalShared.LIKERT_RADIO:
                // customization handled at group level
                break;
            default:
                break;
        }
        applyFormatObj(tempObj);
    };

    const onCheckBoxChange = (event, fieldName) => {
        const checked = event.target.checked;
        const tempObj = { ...formatObj, [fieldName]: checked };
        applyFormatObj(tempObj);
    };

    const setColor = (fieldName, colorObj) => {
        const isButtonObj = fieldName.toLowerCase().includes("button")
        const tempObj = { ...formatObj };
        let target = ["text", "numbers"].includes(fieldName) ? tempObj[fieldName].colorObj : tempObj[fieldName];
        if (isButtonObj) target = tempObj.buttonObj[fieldName]
        target.colorType = colorObj.colorType;
        target.colorIndex = colorObj.colorIndex;
        target.colorEntry = colorObj.colorEntry;
        target.color = colorObj.colorType === "select" ? colorObj.color : colorObj.colorEntry;
        target.opacity = colorObj.opacity;
        target.opacityEntry = colorObj.opacityEntry;
        applyFormatObj(tempObj);
    };

    const setLabelMetadataValue = (void1, void2, void3, void4, customTextObj) => {
        const mpo = props.metadataPropsObj;
        if (!mpo) return;
        const metadata = JSON.parse(
            mpo.template.metadata[portalShared.CUSTOM_DDO_QUE_LABEL] || "{}"
        );
        portalShared.setObj(metadata, customTextObj);
        const templateClone = portalShared.cloneObj(mpo.template);
        templateClone.metadata[portalShared.CUSTOM_DDO_QUE_LABEL] = JSON.stringify(
            metadata
        );
        mpo.templateUpdateFunction(templateClone, props.questionDepth);
    };

    // Provided so ArgoSelectPortalTextDisplayType can push formatObj updates directly (parity)
    const setStateFormatObj = (next) => setFormatObj(next);

    const displayTextEntry = () => (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <ArgoTextFieldSelectHook
                fileName="ArgoQuestionFormatCustom"
                fieldName="textStyle"
                label="Text Style"
                value={formatObj.textStyle}
                onChange={onSelectChange}
                width="150px"
                menuItems={defaultOrCustomList}
            />
            <div style={{ width: "20px" }}></div>
            {formatObj.textStyle === "custom" ? (
                <ArgoTextDisplayCustomHook
                    templateType={TEMPLATE.TYPE.QUESTION}
                    textEntryType={TEMPLATE.TEXT_TYPE.QUESTION.ANSWER_TEXT}
                    fieldName="textStyle"
                    fieldTextObjName="useCustomObj.text.NOTE-metadata-function-setCustomMetadata-uses-this"
                    initToObj={formatObj.text}
                    metadataPropsObj={props.metadataPropsObj}
                    indent={false}
                    paddingTop="-20px"
                    showDisplay={false}
                    showOpacity={false}
                    showColor={false}
                />
            ) : null}
        </div>
    );

    const getAddressRow = (objName) => {
        const obj = formatObj[objName];
        if (!obj) return null;

        return (
            <div style={{ display: "flex", alignItems: "left", width: "100%", backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX }}>
                <div style={{ width: "20px" }}></div>
                <ArgoTextFieldSelectHook
                    fileName="ArgoQuestionFormatCustom"
                    fieldName={`${objName}.widthType`}
                    label="Width Format"
                    value={obj.widthType}
                    onChange={onSelectChange}
                    width="150px"
                    menuItems={widthFormatList}
                />
                <div style={{ width: "20px" }}></div>
                <ArgoTextFieldHook
                    fileName="ArgoQuestionFormatCustom"
                    fieldName={`${objName}.widthEntry`}
                    label={obj.widthType === "pixels" ? "Width (px)" : "Width (%)"}
                    value={obj.widthEntry}
                    type="number"
                    onChange={onTextChange}
                    width="150px"
                />
                <div style={{ width: "20px" }}></div>
                {objName !== "stateObj" ? (
                    <ArgoTextFieldHook
                        fileName="ArgoQuestionFormatCustom"
                        fieldName={`${objName}.max`}
                        label="Max Characters"
                        value={obj.max}
                        type="number"
                        onChange={onTextChange}
                        width="150px"
                    />
                ) : (
                    <div style={{ width: "150px" }}></div>
                )}
                <div style={{ width: "20px" }}></div>
                <ArgoTextFieldHook
                    fileName="ArgoQuestionFormatCustom"
                    fieldName={`${objName}.floatingLabel`}
                    label="Floating Label"
                    value={obj.floatingLabel.text}
                    onChange={onTextChange}
                    width="250px"
                />
                <div style={{ width: "20px" }}></div>
                {objName !== "stateObj" ? (
                    <ArgoTextFieldHook
                        fileName="ArgoQuestionFormatCustom"
                        fieldName={`${objName}.placeHolder`}
                        label="Place Holder"
                        value={obj.placeHolder}
                        onChange={onTextChange}
                        width="250px"
                    />
                ) : (
                    <div style={{ width: "250px" }}></div>
                )}
                <div style={{ width: "20px" }}></div>
                {formatObj.labelPosition !== "none" ? (
                    <ArgoTextFieldHook
                        fileName="ArgoQuestionFormatCustom"
                        fieldName={`${objName}.label`}
                        label="Label"
                        value={obj.label}
                        onChange={onTextChange}
                        width="200px"
                        disabled={obj.labelPosition === "none"}
                    />
                ) : null}
            </div>
        );
    };

    const formatCustomOptions = () => {
        const qType = props.questionType;
        let customColorsArray = [];
        let customColorPropsArray = [];
        let customLabelTextObj;
        if (
            props.metadataPropsObj &&
            props.metadataPropsObj.template &&
            props.metadataPropsObj.template.metadata &&
            props.metadataPropsObj.template.metadata.hasOwnProperty(
                portalShared.CUSTOM_DDO_QUE_LABEL
            )
        ) {
            try {
                customLabelTextObj = JSON.parse(
                    props.metadataPropsObj.metadata?.[portalShared.CUSTOM_DDO_QUE_LABEL] ||
                        "{}"
                );
            } catch (e) {
                customLabelTextObj = {};
            }
        }

        switch (qType) {
            case portalShared.FREE_TEXT:
            case portalShared.EMAIL:
            case portalShared.PHONE:
            case portalShared.NUMBER:
            case portalShared.CURRENCY:
            case portalShared.ZIPPOSTAL_ONLY:
            case portalShared.DATE:
            case portalShared.DATE_RANGE: {
                customColorsArray = customColorTitlesFreeText;
                customColorPropsArray = customColorPropsFreeText;
                if (formatObj.style === "filled") {
                    customColorsArray = [
                        ...customColorTitlesFreeText,
                        "Selected Background",
                        "Hover Background",
                        "Unselected Background"
                    ];
                    customColorPropsArray = [
                        ...customColorPropsFreeText,
                        "selectedBackground",
                        "hoverBackground",
                        "unselectedBackground"
                    ];
                }
                return (
                    <React.Fragment>
                        <div style={{ width: "100%", backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX }}>
                            <div style={{ height: "10px" }}>&nbsp;</div>
                            <div style={{ display: "flex", alignItems: "left", width: "100%" }}>
                                <div style={{ width: "20px" }}></div>
                                <ArgoTextFieldSelectHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="style"
                                    label="Style"
                                    value={formatObj.style}
                                    onChange={onSelectChange}
                                    width="150px"
                                    menuItems={styleList}
                                />
                                <div style={{ width: "20px" }}></div>
                                <ArgoTextFieldSelectHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="size"
                                    label="Size"
                                    value={formatObj.size}
                                    onChange={onSelectChange}
                                    width="150px"
                                    menuItems={sizeList}
                                />
                                <div style={{ width: "20px" }}></div>
                                <ArgoTextFieldSelectHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="widthType"
                                    label="Width Format"
                                    value={formatObj.widthType}
                                    onChange={onSelectChange}
                                    width="150px"
                                    menuItems={widthFormatList}
                                />
                                <div style={{ width: "20px" }}></div>
                                <ArgoTextFieldHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="widthEntry"
                                    label={formatObj.widthType === "pixels" ? "Width (px)" : "Width (%)"}
                                    value={formatObj.widthEntry}
                                    type="number"
                                    onChange={onTextChange}
                                    width="70px"
                                />
                                <div style={{ width: "20px" }}></div>
                                {![portalShared.DATE, portalShared.DATE_RANGE, portalShared.PHONE].includes(qType) ? (
                                    <div style={{ display: "flex" }}>
                                        <ArgoTextFieldHook
                                            fileName="ArgoQuestionFormatCustom"
                                            fieldName="max"
                                            label="Max Characters"
                                            value={formatObj.max}
                                            type="number"
                                            onChange={onTextChange}
                                            width="100px"
                                        />
                                        <div style={{ width: "20px" }}></div>
                                        {qType === portalShared.FREE_TEXT ? (
                                            <div style={{ display: "flex" }}>
                                                <ArgoCheckboxHook
                                                    fileName="ArgoQuestionFormatCustom"
                                                    fieldName="multiline"
                                                    label="Multiline"
                                                    onChange={onCheckBoxChange}
                                                    checked={formatObj.multiline}
                                                    paddingTop="15px"
                                                    width="110px"
                                                />
                                                <div style={{ width: "20px" }}></div>
                                                <ArgoTextFieldHook
                                                    fileName="ArgoQuestionFormatCustom"
                                                    fieldName="minRows"
                                                    label="Min Rows"
                                                    value={formatObj.minRows}
                                                    type="number"
                                                    onChange={onTextChange}
                                                    width="80px"
                                                    disabled={!formatObj.multiline}
                                                />
                                                <div style={{ width: "20px" }}></div>
                                                <ArgoTextFieldHook
                                                    fileName="ArgoQuestionFormatCustom"
                                                    fieldName="maxRows"
                                                    label="Max Rows"
                                                    value={formatObj.maxRows}
                                                    type="number"
                                                    onChange={onTextChange}
                                                    width="80px"
                                                    disabled={!formatObj.multiline}
                                                />
                                                <div style={{ width: "20px" }}></div>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : (
                                    <div>
                                        {qType === portalShared.DATE_RANGE ? (
                                            <div style={{ display: "flex" }}>
                                                <ArgoTextFieldSelectHook
                                                    fileName="ArgoQuestionFormatCustom"
                                                    fieldName="arrangeDateRange"
                                                    label="Arrange"
                                                    value={formatObj.arrangeDateRange}
                                                    onChange={onSelectChange}
                                                    width="150px"
                                                    menuItems={arrangeList}
                                                />
                                                <div style={{ width: "20px" }}></div>
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                                <ArgoTextFieldHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="floatingLabel"
                                    label={qType === portalShared.DATE_RANGE ? "Floating Label From" : "Floating Label"}
                                    value={formatObj.unselectedFloatingLabel?.text}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        const tmp = { ...formatObj };
                                        if (!tmp.unselectedFloatingLabel) tmp.unselectedFloatingLabel = { text: "" };
                                        tmp.unselectedFloatingLabel.text = val;
                                        applyFormatObj(tmp);
                                    }}
                                    width="230px"
                                />
                                {qType === portalShared.DATE_RANGE ? (
                                    <div style={{ display: "flex" }}>
                                        <div style={{ width: "20px" }}></div>
                                        <ArgoTextFieldHook
                                            fileName="ArgoQuestionFormatCustom"
                                            fieldName="floatingLabel2"
                                            label="Floating Label To"
                                            value={formatObj.floatingLabel2}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const tmp = { ...formatObj };
                                                tmp.floatingLabel2 = val;
                                                applyFormatObj(tmp);
                                            }}
                                            width="230px"
                                        />
                                    </div>
                                ) : null}
                                <div style={{ width: "20px" }}></div>
                                {![portalShared.DATE, portalShared.DATE_RANGE, portalShared.PHONE].includes(qType) ? (
                                    <div>
                                        <ArgoTextFieldHook
                                            fileName="ArgoQuestionFormatCustom"
                                            fieldName="placeHolder"
                                            label="Place Holder"
                                            value={formatObj.placeHolder?.text}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const tmp = { ...formatObj };
                                                if (!tmp.placeHolder) tmp.placeHolder = { text: "" };
                                                tmp.placeHolder.text = val;
                                                applyFormatObj(tmp);
                                            }}
                                            width="230px"
                                        />
                                    </div>
                                ) : null}
                            </div>
                            <div style={{ display: "flex", alignItems: "left", width: "100%" }}>
                                <div style={{ width: "20px" }}></div>
                                <ArgoTextFieldSelectHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="labelPosition"
                                    label="Label Position"
                                    value={formatObj.labelPosition}
                                    onChange={onSelectChange}
                                    width="150px"
                                    menuItems={labelPositionList}
                                />
                                <div style={{ width: "20px" }}></div>
                                <ArgoTextFieldHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="label"
                                    label={qType === portalShared.DATE_RANGE ? "Label From" : "Label"}
                                    value={formatObj.label}
                                    onChange={onTextChange}
                                    width="200px"
                                    disabled={formatObj.labelPosition === "none"}
                                />
                                {qType === portalShared.DATE_RANGE ? (
                                    <div style={{ display: "flex" }}>
                                        <div style={{ width: "20px" }}></div>
                                        <ArgoTextFieldHook
                                            fileName="ArgoQuestionFormatCustom"
                                            fieldName="label2"
                                            label="Label To"
                                            value={formatObj.label2}
                                            onChange={onTextChange}
                                            width="200px"
                                            disabled={formatObj.labelPosition === "none"}
                                        />
                                    </div>
                                ) : null}
                                {formatObj.labelPosition !== "none" ? (
                                    <ArgoSelectPortalTextDisplayTypeHook
                                        templateType={TEMPLATE.TYPE.QUESTION}
                                        textEntryType={TEMPLATE.TEXT_TYPE.QUESTION.LABEL}
                                        fieldName={props.labelFieldName}
                                        labelText="Label Display"
                                        hideOption={false}
                                        initTo={formatObj.labelDisplay}
                                        fieldTextObjName="labelDisplay"
                                        metadataPropsObj={props.metadataPropsObj}
                                        setCustomMetadata={setLabelMetadataValue}
                                        questionDepth={props.questionDepth}
                                        setStateFormatObj={setStateFormatObj}
                                    />
                                ) : null}
                            </div>
                            <div style={{ display: "flex", alignItems: "left", width: "100%" }}>
                                {formatObj.labelDisplay === "custom" && formatObj.labelPosition !== "none" ? (
                                    <ArgoTextDisplayCustomHook
                                        fieldName="labelDisplayDefinition"
                                        fieldTextObjName={props.fieldTextObjName}
                                        showDisplay={false}
                                        initToObj={customLabelTextObj}
                                        metadataTag={portalShared.CUSTOM_DDO_QUE_LABEL}
                                        metadataPropsObj={props.metadataPropsObj}
                                        setCustomMetadata={setLabelMetadataValue}
                                    />
                                ) : null}
                            </div>
                            <div style={{ display: "flex", alignItems: "left", width: "100%" }}>
                                <div style={{ width: "20px" }}></div>
                                {displayTextEntry()}
                            </div>
                            <div style={{ display: "flex", alignItems: "left", width: "100%" }}>
                                <div style={{ width: "20px" }}></div>
                                <ArgoTextFieldSelectHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="color"
                                    label="Input Color"
                                    value={formatObj.color}
                                    onChange={onSelectChange}
                                    width="150px"
                                    menuItems={defaultOrCustomList}
                                />
                                <div style={{ width: "20px" }}></div>
                                {qType === portalShared.NUMBER ? (
                                    <React.Fragment>
                                        <ArgoCheckboxHook
                                            fileName="thousandSeparator"
                                            fieldName="thousandSeparator"
                                            label="Separate Thousands"
                                            onChange={onCheckBoxChange}
                                            checked={formatObj.thousandSeparator}
                                            paddingTop="25px"
                                            width="200px"
                                        />
                                        <ArgoCheckboxHook
                                            fileName="allowNegatives"
                                            fieldName="isNegative"
                                            label="Allow negatives"
                                            onChange={onCheckBoxChange}
                                            checked={formatObj.isNegative}
                                            paddingTop="25px"
                                            width="165px"
                                        />
                                        <ArgoCheckboxHook
                                            fileName="allowPercentage"
                                            fieldName="isPercentage"
                                            label="Is Percentage"
                                            onChange={onCheckBoxChange}
                                            checked={formatObj.isPercentage}
                                            paddingTop="25px"
                                            width="150px"
                                        />
                                    </React.Fragment>
                                ) : null}
                                {(qType === portalShared.NUMBER || qType === portalShared.CURRENCY) && (
                                    <React.Fragment>
                                        <ArgoCheckboxHook
                                            fileName="allowDecimal"
                                            fieldName="isDecimal"
                                            label="Is Decimal"
                                            onChange={onCheckBoxChange}
                                            checked={formatObj.isDecimal}
                                            paddingTop="25px"
                                            width="130px"
                                        />
                                        {qType === portalShared.NUMBER && formatObj.isDecimal ? (
                                            <ArgoTextFieldSelectHook
                                                fileName="decimalScale"
                                                fieldName="decimalScale"
                                                label="Decimal Places"
                                                value={formatObj.decimalScale}
                                                onChange={onSelectChange}
                                                width="170px"
                                                menuItems={decimalPlacesList}
                                            />
                                        ) : null}
                                    </React.Fragment>
                                )}
                                {qType === portalShared.ZIPPOSTAL_ONLY ? (
                                    <ArgoTextFieldSelectHook
                                        fileName="zipFormat"
                                        fieldName="zipFormat"
                                        label="ZIP Format"
                                        value={formatObj.zipFormat}
                                        onChange={onSelectChange}
                                        width="170px"
                                        menuItems={zipFormatList}
                                    />
                                ) : null}
                            </div>
                            {props.labelInitTo === "custom" && formatObj.labelPosition !== "none" ? (
                                <ArgoTextDisplayCustomHook
                                    fieldName="labelDisplayDefinition"
                                    fieldTextObjName={props.fieldTextObjName}
                                    initToObj={props.labelInitToObj}
                                    metadataTag={portalShared.CUSTOM_DDO_QUE_LABEL}
                                    metadataPropsObj={props.metadataPropsObj}
                                    setCustomMetadata={setLabelMetadataValue}
                                />
                            ) : null}
                            {formatObj.color === "custom" ? (
                                <React.Fragment>
                                    {customColorsArray.map((title, index) => (
                                        <div
                                            key={title + index}
                                            style={{
                                                display: "flex",
                                                alignItems: "left",
                                                width: "100%",
                                                backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX
                                            }}
                                        >
                                            <div style={{ width: "40px" }}></div>
                                            <ArgoColorInputHook
                                                mode="questionSingleSelect"
                                                colorLabel={title}
                                                propsFieldName={customColorPropsArray[index]}
                                                index={props.index}
                                                initToObj={formatObj[customColorPropsArray[index]]}
                                                setColor={setColor}
                                                opacityFlag={true}
                                            />
                                        </div>
                                    ))}
                                </React.Fragment>
                            ) : null}
                        </div>
                    </React.Fragment>
                );
            }
            case portalShared.ADDRESS_FULL: {
                customColorsArray = customColorTitlesFreeText;
                customColorPropsArray = customColorPropsFreeText;
                if (formatObj.style === "filled") {
                    customColorsArray = [
                        ...customColorTitlesFreeText,
                        "Selected Background",
                        "Hover Background",
                        "Unselected Background"
                    ];
                    customColorPropsArray = [
                        ...customColorPropsFreeText,
                        "selectedBackground",
                        "hoverBackground",
                        "unselectedBackground"
                    ];
                }
                return (
                    <React.Fragment>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "left",
                                width: "100%",
                                backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX,
                                paddingTop: "20px"
                            }}
                        >
                            <div style={{ width: "20px" }}></div>
                            <ArgoTextFieldSelectHook
                                fileName="ArgoQuestionFormatCustom"
                                fieldName="style"
                                label="Style"
                                value={formatObj.style}
                                onChange={onSelectChange}
                                width="150px"
                                menuItems={styleList}
                            />
                            <div style={{ width: "20px" }}></div>
                            <ArgoTextFieldSelectHook
                                fileName="ArgoQuestionFormatCustom"
                                fieldName="size"
                                label="Size"
                                value={formatObj.size}
                                onChange={onSelectChange}
                                width="150px"
                                menuItems={sizeList}
                            />
                            <div style={{ width: "20px" }}></div>
                            <ArgoTextFieldSelectHook
                                fileName="ArgoQuestionFormatCustom"
                                fieldName="labelPosition"
                                label="Label Position"
                                value={formatObj.labelPosition}
                                onChange={onSelectChange}
                                width="150px"
                                menuItems={labelPositionList}
                            />
                        </div>
                        <div>{getAddressRow("street1Obj")}</div>
                        <div>{getAddressRow("street2Obj")}</div>
                        <div>{getAddressRow("cityObj")}</div>
                        <div>{getAddressRow("stateObj")}</div>
                        <div>{getAddressRow("zipObj")}</div>
                        {formatObj.labelPosition !== "none" ? (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    width: "100%",
                                    backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX
                                }}
                            >
                                <div style={{ width: "5px" }}></div>
                                <ArgoSelectPortalTextDisplayTypeHook
                                    templateType={TEMPLATE.TYPE.QUESTION}
                                    textEntryType={TEMPLATE.TEXT_TYPE.QUESTION.LABEL}
                                    fieldName={props.labelFieldName}
                                    labelText="Label Display"
                                    hideOption={false}
                                    initTo={formatObj.labelDisplay}
                                    fieldTextObjName="labelDisplay"
                                    metadataPropsObj={props.metadataPropsObj}
                                    setCustomMetadata={setLabelMetadataValue}
                                    questionDepth={props.questionDepth}
                                    setStateFormatObj={setStateFormatObj}
                                    width="150px"
                                />
                                <div style={{ width: "3px" }}></div>
                                <div>
                                    {formatObj.labelDisplay === "custom" && formatObj.labelPosition !== "none" ? (
                                        <ArgoTextDisplayCustomHook
                                            fieldName="labelDisplayDefinition"
                                            fieldTextObjName={props.fieldTextObjName}
                                            showDisplay={false}
                                            initToObj={customLabelTextObj}
                                            metadataTag={portalShared.CUSTOM_DDO_QUE_LABEL}
                                            metadataPropsObj={props.metadataPropsObj}
                                            setCustomMetadata={setLabelMetadataValue}
                                            paddingTop="0px"
                                        />
                                    ) : null}
                                </div>
                            </div>
                        ) : null}
                        <div style={{ display: "flex", alignItems: "left", width: "100%", backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX }}>
                            <div style={{ width: "20px" }}></div>
                            <ArgoTextFieldSelectHook
                                fileName="ArgoQuestionFormatCustom"
                                fieldName="color"
                                label="Format"
                                value={formatObj.color}
                                onChange={onSelectChange}
                                width="150px"
                                menuItems={defaultOrCustomList}
                            />
                        </div>
                        {formatObj.color === "custom" ? (
                            <React.Fragment>
                                {customColorsArray.map((title, index) => (
                                    <div
                                        key={title + index}
                                        style={{
                                            display: "flex",
                                            alignItems: "left",
                                            width: "100%",
                                            backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX
                                        }}
                                    >
                                        <div style={{ width: "40px" }}></div>
                                        <ArgoColorInputHook
                                            mode="questionSingleSelect"
                                            colorLabel={title}
                                            propsFieldName={customColorPropsArray[index]}
                                            index={props.index}
                                            initToObj={formatObj[customColorPropsArray[index]]}
                                            setColor={setColor}
                                            opacityFlag={customColorPropsArray[index] === "placeHolder"}
                                        />
                                    </div>
                                ))}
                            </React.Fragment>
                        ) : null}
                    </React.Fragment>
                );
            }
            case portalShared.STATE_ONLY: {
                customColorsArray = customColorTitlesDDLB;
                customColorPropsArray = customColorPropsDDLB;

                return (
                    <React.Fragment>
                        <div style={{ alignItems: "left", width: "100%", backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX, paddingTop: "20px" }}>
                            <div style={{display: "flex"}}>
                                <div style={{width: "20px"}}></div>
                                <ArgoTextFieldSelectHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="displayAs"
                                    label="Display As"
                                    value={formatObj.displayAs}
                                    onChange={onSelectChange}
                                    width="150px"
                                    menuItems={[
                                        {value: "single", text: "Single Select"},
                                        {value: "likert", text: "Likert"}
                                    ]}
                                    disabled={props.answerCount > 0}
                                />

                                <div style={{width: "20px"}}></div>

                                <ArgoTextFieldSelectHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="style"
                                    label="Style"
                                    value={formatObj.style}
                                    onChange={onSelectChange}
                                    width="160px"
                                    menuItems={[
                                        {value: "radios", text: "Radios"},
                                        {value: "outlined", text: "Outlined Radios"},
                                        {value: "ddlb", text: "DDLB"},
                                        {value: "standard", text: "Standard Buttons"},
                                        {value: "clear", text: "Clear Buttons"}
                                    ]}
                                    disabled={true}
                                />

                                <div style={{width: "20px"}}></div>

                                {
                                    formatObj.style === "ddlb" ?
                                        <React.Fragment>
                                            <ArgoTextFieldSelectHook
                                                fileName="ArgoQuestionFormatCustom"
                                                fieldName="ddlbStyle"
                                                label="DDLB Style"
                                                value={formatObj.ddlbStyle}
                                                onChange={onSelectChange}
                                                width="160px"
                                                menuItems={[
                                                    {value: "outlined", text: "Outlined"},
                                                    {value: "filled", text: "Filled"},
                                                    {value: "standard", text: "Standard"},
                                                ]}
                                            />

                                            <div style={{width: "20px"}}></div>

                                            <ArgoTextFieldHook
                                                fileName="ArgoQuestionFormatCustom"
                                                fieldName="floatingLabel"
                                                label="Floating Label"
                                                value={formatObj.floatingLabel?.text}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const tmp = {...formatObj};
                                                    tmp.floatingLabel.text = value;
                                                    applyFormatObj(tmp);
                                                }}
                                                width="230px"
                                            />

                                            <div style={{width: "20px"}}></div>
                                        </React.Fragment> : ""
                                }
                                {
                                    ["ddlb", "outlined", "standard", "clear"].includes(formatObj.style) ?
                                        <React.Fragment>
                                            <ArgoTextFieldSelectHook
                                                fileName="ArgoQuestionFormatCustom"
                                                fieldName="widthType"
                                                label="Width Format"
                                                value={formatObj.widthType}
                                                onChange={onSelectChange}
                                                width="150px"
                                                menuItems={widthFormatList}
                                            />

                                            <div style={{width: "20px"}}></div>

                                            <ArgoTextFieldHook
                                                fileName="ArgoQuestionFormatCustom"
                                                fieldName="widthEntry"
                                                label={formatObj.widthType === "pixels" ? "Width (px)" : "Width (%)"}
                                                value={formatObj.widthEntry}
                                                type="number"
                                                onChange={onTextChange}
                                                width="150px"
                                                minNum={40}
                                            />
                                        </React.Fragment> : ""
                                }
                            </div>
                            <React.Fragment>
                                <div style={{ display: "flex" }}>
                                    <div style={{ width: "20px" }}></div>
                                    <ArgoTextFieldSelectHook
                                        fileName="ArgoQuestionFormatCustom"
                                        fieldName="color"
                                        label="Color"
                                        value={formatObj.color}
                                        onChange={onSelectChange}
                                        width="150px"
                                        menuItems={defaultOrCustomList}
                                    />
                                </div>
                            </React.Fragment>
                        </div>

                        {formatObj.color === "custom" ? (
                            <React.Fragment>
                                {customColorsArray.map((title, index) => (
                                    <div
                                        key={title + index}
                                        style={{
                                            display: "flex",
                                            alignItems: "left",
                                            width: "100%",
                                            backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX
                                        }}
                                    >
                                        <div style={{ width: "40px" }}></div>
                                        <ArgoColorInputHook
                                            mode="questionSingleSelect"
                                            colorLabel={title}
                                            propsFieldName={customColorPropsArray[index]}
                                            index={props.index}
                                            initToObj={formatObj[customColorPropsArray[index]]}
                                            setColor={setColor}
                                        />
                                    </div>
                                ))}
                            </React.Fragment>
                        ) : null}
                    </React.Fragment>
                )
            }
            case portalShared.CHECKBOX: {
                customColorsArray = customColorRadiosAndCheckboxesTitles;
                customColorPropsArray = customColorRadiosAndCheckboxesProps;
                return (
                    <React.Fragment>
                        <div
                            style={{
                                alignItems: "left",
                                width: "100%",
                                backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX
                            }}
                        >
                            <div style={{ display: "flex" }}>
                                <div style={{ width: "20px" }}></div>
                                <ArgoTextFieldSelectHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="style"
                                    label="Style"
                                    value={formatObj.style}
                                    onChange={onSelectChange}
                                    width="150px"
                                    menuItems={[{ value: "checkboxes", text: "Checkboxes" }]}
                                />

                                <div style={{ width: "20px" }}></div>
                                <ArgoTextFieldSelectHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="arrange"
                                    label="Arrange"
                                    value={formatObj.arrange}
                                    onChange={onSelectChange}
                                    width="150px"
                                    menuItems={arrangeList}
                                />
                            </div>


                            <div style={{ display: "flex", alignItems: "left", width: "100%"}}>
                                <div style={{ width: "20px" }}></div>
                                {displayTextEntry()}
                            </div>

                            <div style={{ display: "flex", alignItems: "left", width: "100%", backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX }}>
                                <div style={{ width: "20px" }}></div>
                                <ArgoTextFieldSelectHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="color"
                                    label="Color"
                                    value={formatObj.color}
                                    onChange={onSelectChange}
                                    width="150px"
                                    menuItems={defaultOrCustomList}
                                />
                            </div>
                        </div>
                        {formatObj.color === "custom" ? (
                            <React.Fragment>
                                {customColorsArray.map((title, index) => (
                                    <div
                                        key={title + index}
                                        style={{
                                            display: "flex",
                                            alignItems: "left",
                                            width: "100%",
                                            backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX
                                        }}
                                    >
                                        <div style={{ width: "40px" }}></div>
                                        <ArgoColorInputHook
                                            mode="questionSingleSelect"
                                            colorLabel={title}
                                            propsFieldName={customColorPropsArray[index]}
                                            index={props.index}
                                            initToObj={formatObj[customColorPropsArray[index]]}
                                            setColor={setColor}
                                            opacityFlag={customColorPropsArray[index] === "placeHolder"}
                                        />
                                    </div>
                                ))}
                            </React.Fragment>
                        ) : null}
                    </React.Fragment>
                );
            }
            case portalShared.RADIO:
            case portalShared.YES_NO: {
                switch (formatObj.style) {
                    case "radios":
                        customColorsArray = customColorRadiosAndCheckboxesTitles;
                        customColorPropsArray = customColorRadiosAndCheckboxesProps;
                        break;
                    case "outlined":
                        customColorsArray = customColorTitlesOutlined;
                        customColorPropsArray = customColorPropsOutlined;
                        break;
                    case "ddlb":
                        customColorsArray = customColorTitlesDDLB;
                        customColorPropsArray = customColorPropsDDLB;
                        break;
                    case "standard":
                    case "clear":
                        customColorsArray = customColorTitlesNoControl;
                        customColorPropsArray = customColorPropsNoControl;
                        break;
                    default:
                        break;
                }
                return (
                    <React.Fragment>
                        <div style={{ alignItems: "left", width: "100%", backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX, paddingTop: "20px" }}>
                            <div style={{ display: "flex" }}>
                                <div style={{ width: "20px" }}></div>
                                <ArgoTextFieldSelectHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="displayAs"
                                    label="Display As"
                                    value={formatObj.displayAs}
                                    onChange={onSelectChange}
                                    width="150px"
                                    menuItems={[
                                        { value: "single", text: "Single Select" },
                                        { value: "likert", text: "Likert" }
                                    ]}
                                    disabled={props.answerCount > 0}
                                />
                                <div style={{ width: "20px" }}></div>
                                {formatObj.displayAs === "single" ? (
                                    <ArgoTextFieldSelectHook
                                        fileName="ArgoQuestionFormatCustom"
                                        fieldName="style"
                                        label="Style"
                                        value={formatObj.style}
                                        onChange={onSelectChange}
                                        width="160px"
                                        menuItems={[
                                            { value: "radios", text: "Radios" },
                                            { value: "outlined", text: "Outlined Radios" },
                                            { value: "ddlb", text: "DDLB" },
                                            { value: "standard", text: "Standard Buttons" },
                                            { value: "clear", text: "Clear Buttons" }
                                        ]}
                                    />
                                ) : null}
                                <div style={{ width: "20px" }}></div>
                                {formatObj.displayAs === "single" ? (
                                    <React.Fragment>
                                        {
                                            ["radios", "outlined", "standard", "clear"].includes(formatObj.style) ?
                                                <React.Fragment>
                                                    <ArgoTextFieldSelectHook
                                                        fileName="ArgoQuestionFormatCustom"
                                                        fieldName="arrange"
                                                        label="Arrange"
                                                        value={formatObj.arrange}
                                                        onChange={onSelectChange}
                                                        width="150px"
                                                        menuItems={arrangeList}
                                                    />
                                                    <div style={{width: "20px"}}></div>
                                                </React.Fragment>: ""
                                        }
                                        {
                                            formatObj.style === "ddlb" ?
                                                <React.Fragment>
                                                    <ArgoTextFieldSelectHook
                                                        fileName="ArgoQuestionFormatCustom"
                                                        fieldName="ddlbStyle"
                                                        label="DDLB Style"
                                                        value={formatObj.ddlbStyle}
                                                        onChange={onSelectChange}
                                                        width="160px"
                                                        menuItems={[
                                                            { value: "outlined", text: "Outlined" },
                                                            { value: "filled", text: "Filled" },
                                                            { value: "standard", text: "Standard" },
                                                        ]}
                                                    />

                                                    <div style={{width: "20px"}}></div>

                                                    <ArgoTextFieldHook
                                                        fileName="ArgoQuestionFormatCustom"
                                                        fieldName="floatingLabel"
                                                        label="Floating Label"
                                                        value={formatObj.floatingLabel?.text}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            const tmp = {...formatObj};
                                                            tmp.floatingLabel.text = value;
                                                            applyFormatObj(tmp);
                                                        }}
                                                        width="230px"
                                                    />

                                                    <div style={{width: "20px"}}></div>
                                                </React.Fragment> : ""
                                        }
                                        {
                                            ["ddlb", "outlined", "standard", "clear"].includes(formatObj.style) ?
                                                <React.Fragment>
                                                    <ArgoTextFieldSelectHook
                                                        fileName="ArgoQuestionFormatCustom"
                                                        fieldName="widthType"
                                                        label="Width Format"
                                                        value={formatObj.widthType}
                                                        onChange={onSelectChange}
                                                        width="150px"
                                                        menuItems={widthFormatList}
                                                    />

                                                    <div style={{width: "20px"}}></div>

                                                    <ArgoTextFieldHook
                                                        fileName="ArgoQuestionFormatCustom"
                                                        fieldName="widthEntry"
                                                        label={formatObj.widthType === "pixels" ? "Width (px)" : "Width (%)"}
                                                        value={formatObj.widthEntry}
                                                        type="number"
                                                        onChange={onTextChange}
                                                        width="150px"
                                                        minNum={40}
                                                    />

                                                    <div style={{width: "20px"}}></div>
                                                </React.Fragment> : ""
                                        }
                                        {
                                            ["radios", "outlined", "standard", "clear"].includes(formatObj.style) && formatObj.arrange === "side" ?
                                                <React.Fragment>
                                                    <div>
                                                        <ArgoCheckboxHook
                                                            fileName="ArgoQuestionFormatCustom"
                                                            fieldName="alignAnswersRight"
                                                            label="Align answers right"
                                                            onChange={onSelectChange}
                                                            checked={formatObj.alignAnswersRight}
                                                            width="100%"
                                                            paddingTop="20px"
                                                            margin
                                                        />
                                                    </div>

                                                    <div style={{width: "20px"}}></div>

                                                    {formatObj.alignAnswersRight ?
                                                        <div>
                                                            <ArgoCheckboxHook
                                                                fileName="ArgoQuestionFormatCustom"
                                                                fieldName="alignQuestionsAndAnswers"
                                                                label="Align questions with answers"
                                                                onChange={onSelectChange}
                                                                checked={formatObj.alignQuestionsAndAnswers}
                                                                width="100%"
                                                                paddingTop="20px"
                                                            />
                                                        </div> : ""
                                                    }

                                                    <div style={{width: "20px"}}></div>

                                                    {formatObj.alignQuestionsAndAnswers ?
                                                        <ArgoTextFieldHook
                                                            fileName="ArgoQuestionFormatCustom"
                                                            fieldName="indent"
                                                            label="Question Indent (px)"
                                                            value={parseInt(formatObj.indent)}
                                                            type="number"
                                                            onChange={onTextChange}
                                                            width="140px"
                                                        /> : ""
                                                    }
                                                </React.Fragment> : ""
                                        }
                                    </React.Fragment>
                                ) : ""}
                            </div>
                            {formatObj.displayAs === "single" ? (
                                formatObj.style !== "ddlb" ? (
                                    <React.Fragment>
                                        <div style={{ display: "flex" }}>
                                            <div style={{ width: "20px" }}></div>
                                            {displayTextEntry()}
                                        </div>
                                        <div style={{ display: "flex" }}>
                                            <div style={{ width: "20px" }}></div>
                                            <ArgoTextFieldSelectHook
                                                fileName="ArgoQuestionFormatCustom"
                                                fieldName="color"
                                                label="Color"
                                                value={formatObj.color}
                                                onChange={onSelectChange}
                                                width="150px"
                                                menuItems={defaultOrCustomList}
                                            />
                                        </div>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <div style={{ display: "flex" }}>
                                            <div style={{ width: "20px" }}></div>
                                            <ArgoTextFieldSelectHook
                                                fileName="ArgoQuestionFormatCustom"
                                                fieldName="color"
                                                label="Color"
                                                value={formatObj.color}
                                                onChange={onSelectChange}
                                                width="150px"
                                                menuItems={defaultOrCustomList}
                                            />
                                        </div>
                                    </React.Fragment>
                                )
                            ) : null}
                            {formatObj.displayAs === "likert" ? (
                                <ArgoLikertAnswerGeneratorHook
                                    fileName="ArgoQuestionFormatCustom"
                                    componentMode="likert-titles"
                                    templateType={TEMPLATE.TYPE.QUESTION}
                                    template={props.metadataPropsObj.template}
                                    templateUpdateFunction={props.metadataPropsObj.templateUpdateFunction}
                                    isLikert={true}
                                    answerOptionTemplates={props.metadataPropsObj.answerOptionTemplates}
                                    answerErrorCallback={() => {}}
                                    questionDepth={props.questionDepth}
                                    setMetadataErrorText={props.setMetadataErrorText}
                                    customMetadataKey={portalShared.CUSTOM_DDO_QUE_FORMAT}
                                    createMode={props.createMode}
                                />
                            ) : null}
                        </div>
                        {formatObj.color === "custom" ? (
                            <React.Fragment>
                                {customColorsArray.map((title, index) => (
                                    <div
                                        key={title + index}
                                        style={{
                                            display: "flex",
                                            alignItems: "left",
                                            width: "100%",
                                            backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX
                                        }}
                                    >
                                        <div style={{ width: "40px" }}></div>
                                        <ArgoColorInputHook
                                            mode="questionSingleSelect"
                                            colorLabel={title}
                                            propsFieldName={customColorPropsArray[index]}
                                            index={props.index}
                                            initToObj={formatObj[customColorPropsArray[index]]}
                                            setColor={setColor}
                                        />
                                    </div>
                                ))}
                            </React.Fragment>
                        ) : null}
                    </React.Fragment>
                );
            }
            case portalShared.LIKERT_RADIO:
                return (
                    <React.Fragment>
                        <div style={{ display: "flex", alignItems: "left", width: "100%", backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX }}>
                            <ArgoTextFieldHook
                                fileName="ArgoQuestionFormatCustom"
                                fieldName="indent"
                                label="Indent Question Name"
                                value={formatObj.indent}
                                onChange={onTextChange}
                                width="150px"
                            />
                        </div>
                    </React.Fragment>
                );
            case portalShared.RANKING:
                customColorsArray = customColorTitlesRanking;
                customColorPropsArray = customColorPropsRanking;
                return (
                    <React.Fragment>
                        <div style={{
                            alignItems: "left",
                            width: "100%",
                            backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX,
                            paddingTop: "20px"
                        }}>
                            <div style={{display: "flex"}}>
                                <div style={{width: "20px"}}></div>
                                <ArgoTextFieldSelectHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="widthType"
                                    label="Width Format"
                                    value={formatObj.widthType}
                                    onChange={onSelectChange}
                                    width="150px"
                                    menuItems={widthFormatList}
                                />

                                <div style={{width: "20px"}}></div>

                                <ArgoTextFieldHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="widthEntry"
                                    label={formatObj.widthType === "pixels" ? "Width (px)" : "Width (%)"}
                                    value={formatObj.widthEntry}
                                    type="number"
                                    onChange={onTextChange}
                                    width="150px"
                                    minNum={40}
                                />

                                <div style={{width: "20px"}}></div>

                                <ArgoCheckboxHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="numbersEnabled"
                                    label="Enable Numbers"
                                    onChange={onSelectChange}
                                    checked={formatObj.numbersEnabled}
                                    width="160px"
                                    paddingTop={"20px"}
                                    margin
                                />

                                <div style={{width: "20px"}}></div>

                                <ArgoCheckboxHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="showDragIcon"
                                    label="Show Drag Icon"
                                    onChange={onSelectChange}
                                    checked={formatObj.showDragIcon}
                                    width="160px"
                                    paddingTop={"20px"}
                                    margin
                                />
                            </div>

                            <div style={{display: "flex", minHeight: "75px"}}>
                                <div style={{width: "20px"}}></div>
                                <ArgoCheckboxHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="borderEnabled"
                                    label="Enable Border"
                                    onChange={onSelectChange}
                                    checked={formatObj.borderEnabled}
                                    width="150px"
                                    paddingTop={"20px"}
                                    margin
                                />

                                <div style={{width: "20px"}}></div>

                                {formatObj.borderEnabled ?
                                    <React.Fragment>
                                        <ArgoTextFieldHook
                                            fileName="ArgoQuestionFormatCustom"
                                            fieldName="borderWidth"
                                            label={"Border Width (px)"}
                                            value={formatObj.borderWidth}
                                            type="number"
                                            onChange={onTextChange}
                                            width="150px"
                                            minNum={0}
                                        />

                                        <div style={{width: "20px"}}></div>

                                        <ArgoTextFieldHook
                                            fileName="ArgoQuestionFormatCustom"
                                            fieldName="borderRadius"
                                            label={"Border Radius (px)"}
                                            value={formatObj.borderRadius}
                                            type="number"
                                            onChange={onTextChange}
                                            width="150px"
                                            minNum={0}
                                        />

                                        <div style={{width: "20px"}}></div>

                                        <ArgoTextFieldHook
                                            fileName="ArgoQuestionFormatCustom"
                                            fieldName="borderHoverWidth"
                                            label={"Border Hover Width (px)"}
                                            value={formatObj.borderHoverWidth}
                                            type="number"
                                            onChange={onTextChange}
                                            width="150px"
                                            minNum={0}
                                        />

                                        {/*<div style={{width: "20px"}}></div>*/}

                                        {/*<ArgoTextFieldHook*/}
                                        {/*    fileName="ArgoQuestionFormatCustom"*/}
                                        {/*    fieldName="borderHoverRadius"*/}
                                        {/*    label={"Border Hover Radius (px)"}*/}
                                        {/*    value={formatObj.borderHoverRadius}*/}
                                        {/*    type="number"*/}
                                        {/*    onChange={onTextChange}*/}
                                        {/*    width="150px"*/}
                                        {/*    minNum={0}*/}
                                        {/*/>*/}
                                    </React.Fragment> : ""
                                }
                            </div>

                            <div style={{width: "20px"}}></div>

                            <div style={{display: "flex", alignItems: "left", width: "100%", paddingTop: "20px"}}>
                                <div style={{width: "20px"}}></div>
                                {displayTextEntry()}
                            </div>

                            <div style={{display: "flex"}}>
                                <div style={{width: "20px"}}></div>
                                <ArgoTextFieldSelectHook
                                    fileName="ArgoQuestionFormatCustom"
                                    fieldName="color"
                                    label="Color"
                                    value={formatObj.color}
                                    onChange={onSelectChange}
                                    width="150px"
                                    menuItems={defaultOrCustomList}
                                />
                            </div>
                        </div>
                        {formatObj.color === "custom" ? (
                            <React.Fragment>
                                {customColorsArray.map((title, index) => (
                                    <div
                                        key={title + index}
                                        style={{
                                            display: "flex",
                                            alignItems: "left",
                                            width: "100%",
                                            backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX
                                        }}
                                    >
                                        <div style={{width: "40px"}}></div>
                                        {customColorPropsArray[index].includes(".") ?
                                            <ArgoColorInputHook
                                                mode="questionSingleSelect"
                                                colorLabel={title}
                                                propsFieldName={customColorPropsArray[index].split(".")[1]}
                                                index={props.index}
                                                initToObj={customColorPropsArray[index].split(".").reduce((obj, key) => obj && obj[key], formatObj)}
                                                setColor={setColor}
                                            />
                                            :
                                            <ArgoColorInputHook
                                                mode="questionSingleSelect"
                                                colorLabel={title}
                                                propsFieldName={customColorPropsArray[index]}
                                                index={props.index}
                                                initToObj={formatObj[customColorPropsArray[index]]}
                                                setColor={setColor}
                                            />
                                        }

                                    </div>
                                ))}
                            </React.Fragment>
                        ) : null}
                    </React.Fragment>
                )
            default:
                return <React.Fragment/>;
        }
    };

    return <React.Fragment>{formatCustomOptions()}</React.Fragment>;
}

ArgoQuestionFormatCustom.defaultProps = {answerCount: 0, index: 0};

ArgoQuestionFormatCustom.propTypes = {
    questionType: PropTypes.string,
    fieldName: PropTypes.string,
    fieldTextObjName: PropTypes.string,
    index: PropTypes.number,
    initToObj: PropTypes.object,
    metadataTag: PropTypes.string,
    answerCount: PropTypes.number,
    setCustomMetadata: PropTypes.func,
    labelFieldName: PropTypes.string,
    labelInitTo: PropTypes.string,
    labelInitToObj: PropTypes.object,
    labelMetadataTag: PropTypes.string,
    answers: PropTypes.array,
    addAnswerFunction: PropTypes.func,
    metadataPropsObj: PropTypes.object,
    questionDepth: PropTypes.number,
    setMetadataErrorText: PropTypes.func
};

export default ArgoQuestionFormatCustom;

